import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  IconButton,
  Chip,
  Grid,
  Tooltip,
  Button,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  History as HistoryIcon,
  AttachFile as AttachFileIcon,
  Person as PersonIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

const ApprovedPolicyPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // 模拟已批准的保单数据
  const approvedPolicy = {
    id: id,
    policyNumber: 'AUTO-2024-003',
    type: 'Auto Insurance',
    client: 'Michael Brown',
    broker: 'John Smith',
    underwriter: 'Sarah Wilson',
    effectiveDate: '2024-01-30',
    approvedDate: '2024-01-24',
    documents: [
      {
        id: 1,
        name: 'Auto Insurance Application.pdf',
        uploadedAt: '2024-01-20',
        uploadedBy: 'John Smith',
        approvedAt: '2024-01-24',
        approvedBy: 'Sarah Wilson',
        type: 'Application Form'
      },
      {
        id: 2,
        name: 'Driver License.pdf',
        uploadedAt: '2024-01-20',
        uploadedBy: 'John Smith',
        approvedAt: '2024-01-24',
        approvedBy: 'Sarah Wilson',
        type: 'Supporting Document'
      }
    ],
    approvalHistory: [
      {
        date: '2024-01-20',
        author: 'John Smith',
        type: 'submission',
        content: 'Initial submission of all required documents'
      },
      {
        date: '2024-01-22',
        author: 'Sarah Wilson',
        type: 'review',
        content: 'All documents have been reviewed and verified'
      },
      {
        date: '2024-01-24',
        author: 'Sarah Wilson',
        type: 'approval',
        content: 'Policy has been approved and issued'
      }
    ]
  };

  const handleDownload = (doc) => {
    console.log('Downloading document:', doc);
  };

  const handleExportAll = () => {
    console.log('Exporting all records...');
    // 处理导出所有记录的逻辑
  };

  return (
    <Container maxWidth="xl">
      {/* Hero Section */}
      <Box sx={{ 
        pt: 4,
        pb: 3,
        textAlign: 'center',
        position: 'relative',
        borderBottom: '1px solid #e0e0e0',
        mb: 4
      }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/analysis')}
          startIcon={<ArrowBackIcon />}
          sx={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#1976d2',
            borderColor: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)'
            }
          }}
        >
          Back to Analysis
        </Button>

        <Typography 
          variant="h4" 
          sx={{ 
            color: '#1976d2',
            fontWeight: 600,
            mb: 1
          }}
        >
          Approved Policy
        </Typography>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: '#666',
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          View approved policy details and documentation
        </Typography>
      </Box>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column - Policy Information & Documents */}
        <Grid item xs={12} md={7}>
          {/* Policy Information Card */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon /> Policy Information
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Client Name</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{approvedPolicy.client}</Typography>
                
                <Typography variant="body2" color="text.secondary">Broker</Typography>
                <Typography variant="body1">{approvedPolicy.broker}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Effective Date</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{approvedPolicy.effectiveDate}</Typography>
                
                <Typography variant="body2" color="text.secondary">Underwriter</Typography>
                <Typography variant="body1">{approvedPolicy.underwriter}</Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Approved Documents Card */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachFileIcon /> Approved Documents
            </Typography>
            
            {approvedPolicy.documents.map((doc) => (
              <Paper
                key={doc.id}
                elevation={0}
                sx={{
                  p: 2,
                  mb: 2,
                  border: '1px solid #e0e0e0',
                  borderLeft: '4px solid #4caf50',
                  '&:last-child': { mb: 0 }
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {doc.name}
                      </Typography>
                      <Chip 
                        label={doc.type}
                        size="small"
                        sx={{ 
                          bgcolor: '#e8f5e9',
                          color: '#2e7d32',
                          height: '20px'
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Uploaded by {doc.uploadedBy} on {doc.uploadedAt}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#2e7d32', display: 'block' }}>
                      Approved by {doc.approvedBy} on {doc.approvedAt}
                    </Typography>
                  </Box>
                  <Tooltip title="Download">
                    <IconButton 
                      size="small" 
                      onClick={() => handleDownload(doc)}
                      sx={{ color: '#1976d2' }}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            ))}
          </Paper>
        </Grid>

        {/* Right Column - Approval Timeline */}
        <Grid item xs={12} md={5}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon /> Approval Timeline
            </Typography>

            <Box sx={{ position: 'relative' }}>
              {approvedPolicy.approvalHistory.map((event, index) => (
                <Box
                  key={index}
                  sx={{
                    position: 'relative',
                    pl: 4,
                    pb: 4,
                    '&:last-child': { pb: 0 },
                    '&:before': {
                      content: '""',
                      position: 'absolute',
                      left: '12px',
                      top: 0,
                      bottom: 0,
                      width: '2px',
                      bgcolor: index === approvedPolicy.approvalHistory.length - 1 ? 'transparent' : '#e0e0e0'
                    }
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '6px',
                      top: '0',
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      bgcolor: event.type === 'approval' ? '#4caf50' : '#1976d2',
                      border: '2px solid #fff',
                      boxShadow: `0 0 0 2px ${event.type === 'approval' ? '#e8f5e9' : '#e3f2fd'}`
                    }}
                  />
                  
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: event.type === 'approval' ? '#e8f5e9' : '#f8f9fa',
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {event.content}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {event.author}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">•</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {event.date}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ApprovedPolicyPage; 